import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Sparkles, Car, Gem, Shield, Layers, ShieldCheck,
  Sun, Droplets, Wind, Paintbrush
} from "lucide-react";

const services = [
  {
    icon: Sparkles,
    title: "Detailing Interior",
    desc: "Curățare profesională în profunzime a interiorului: tapițerie, piele, plastic, sticlă. Eliminăm petele, mirosurile și bacteriile.",
  },
  {
    icon: Car,
    title: "Detailing Exterior",
    desc: "Spălare profesională, decontaminare, curățare jante și anvelope, tratament cauciuc și finisare impecabilă.",
  },
  {
    icon: Gem,
    title: "Polish Auto",
    desc: "Corecție vopsea în mai multe etape. Eliminăm zgârieturile, hologramele și readucem luciul original al vopselei.",
  },
  {
    icon: Shield,
    title: "Protecție Ceramică",
    desc: "Aplicare coating ceramic profesional Gtechniq Crystal Serum Ultra. Protecție de lungă durată împotriva elementelor.",
  },
  {
    icon: Layers,
    title: "Colantări Auto",
    desc: "Colantare integrală sau parțială. Schimbă culoarea vehiculului tău cu folii premium de la cele mai bune branduri.",
  },
  {
    icon: ShieldCheck,
    title: "Folie PPF",
    desc: "Paint Protection Film - folie transparentă de protecție care apără vopseaua de pietre, zgârieturi și factori externi.",
  },
  {
    icon: Sun,
    title: "Folie Geamuri",
    desc: "Aplicare folie solară profesională. Protecție UV, intimitate sporită și un aspect elegant pentru vehiculul tău.",
  },
  {
    icon: Droplets,
    title: "Spălare Premium",
    desc: "Spălare de lux cu produse profesionale. Tratament spumă activă, decontaminare și uscare cu aer comprimat.",
  },
  {
    icon: Wind,
    title: "Curățare Tapițerie",
    desc: "Curățare cu aburi și extracție profesională pentru tapițerie textilă și piele. Igienizare completă a interiorului.",
  },
  {
    icon: Paintbrush,
    title: "Decontaminare Vopsea",
    desc: "Eliminarea contaminanților încrustați în vopsea: gudron, rășină, particule metalice. Pregătire ideală pentru polish sau coating.",
  },
];

export default function Services() {
  return (
    <div className="pt-20 min-h-screen bg-black" data-testid="services-page">
      {/* Header */}
      <section className="py-20 page-header-bg">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Serviciile <span className="text-red-600">Noastre</span>
          </motion.h1>
          <motion.div
            className="red-line mx-auto mb-6"
            initial={{ width: 0 }}
            animate={{ width: 60 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
          <motion.p
            className="text-base md:text-lg font-light max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{ fontFamily: "'Raleway', sans-serif" }}
          >
            Oferim o gamă completă de servicii profesionale de detailing și protecție auto.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <motion.div
                key={service.title}
                className="shine-card group p-8 bg-[#0A0A0A] border border-[#222] hover:border-red-600 transition-all duration-500"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                viewport={{ once: true }}
                data-testid={`service-card-${i}`}
              >
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <service.icon className="w-12 h-12 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl mb-3">{service.title}</h3>
                    <p className="text-white font-light text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
                      {service.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(220,38,38,0.08),transparent_70%)]" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Interesat de un <span className="text-red-600">Serviciu</span>?
          </motion.h2>
          <motion.p
            className="text-base md:text-lg mb-10 font-light"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            style={{ fontFamily: "'Raleway', sans-serif" }}
          >
            Contactează-ne pentru o ofertă personalizată sau programează o vizită la centrul nostru.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Link
              to="/contact"
              data-testid="services-contact-btn"
              className="px-10 py-4 bg-red-600 text-white hover:bg-red-700 pulse-glow transition-all duration-300 tracking-[0.15em] uppercase text-sm font-medium"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              Solicită Ofertă
            </Link>
            <a
              href="https://wa.me/40761712940"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-whatsapp-btn"
              className="px-10 py-4 border border-white text-white hover:bg-white hover:text-black transition-all duration-300 tracking-[0.15em] uppercase text-sm font-medium"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              WhatsApp
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
